import { useQuery } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { apiClient } from '@/lib/api.ts'
import { Copy, Check, Shield } from 'lucide-react'
import { useState } from 'react'
import {
  Card,
  ErrorState,
  IconButton,
  LoadingState,
} from '@/components/ui'

export function PasswordRetrievePage() {
  const { token } = useParams<{ token: string }>()
  const [copied, setCopied] = useState(false)

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['password-retrieve', token],
    queryFn: () => apiClient.get(`/passwords/retrieve/${token}`).then((r) => r.data),
    enabled: !!token,
    retry: false,
  })

  const copy = async () => {
    if (!data?.password) return
    await navigator.clipboard.writeText(data.password)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const errMsg =
    (error as { response?: { data?: { error?: string } } })?.response?.data?.error ??
    'Link ungültig oder abgelaufen.'

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-sm">
        <Card.Body className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-brand-50 flex items-center justify-center">
              <Shield className="w-5 h-5 text-brand-600" />
            </div>
            <div>
              <h1 className="text-body font-bold text-gray-800">Sicheres Passwort</h1>
              <p className="text-caption text-gray-400">Einmalig abrufbar</p>
            </div>
          </div>

          {isLoading && <LoadingState size="sm" />}

          {isError && <ErrorState title="Passwort nicht abrufbar" description={errMsg} size="sm" />}

          {data?.password && (
            <>
              <div className="bg-gray-50 rounded-md p-3 flex items-center gap-2">
                <code className="flex-1 text-small font-mono text-gray-800 break-all">
                  {data.password}
                </code>
                <IconButton
                  icon={copied ? <Check className="text-green-600" /> : <Copy />}
                  aria-label={copied ? 'Kopiert' : 'Passwort kopieren'}
                  variant="ghost"
                  size="sm"
                  onClick={copy}
                />
              </div>
              <p className="text-caption text-gray-400 text-center">
                Dieses Passwort wurde nach dem Abruf gelöscht.
              </p>
            </>
          )}
        </Card.Body>
      </Card>
    </div>
  )
}
